import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

/**
 * Hook to manage a selected ID in the URL.
 *
 * Be sure to update `main.tsx` to include your optional param in your route.
 * Like the following:
 *
 * ```
 * {
 *   path: "jobs/:jobId?",
 *   element: <Jobs />,
 * }
 * ```
 */
export const useSelectedIdInUrl = (
  paramName: string,
  options?: { prefixSegment?: string }
): {
  selectedId: string | undefined;
  pushIdToUrl: (id: string) => void;
  setSelectedId: (id: string) => void;
  popIdFromUrl: () => void;
} => {
  const params = useParams<Record<string, string | undefined>>();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedId = useMemo(() => params[paramName], [params, paramName]);

  const pushIdToUrl = useCallback(
    (id: string) => {
      const pathSegments = location.pathname.split("/").filter(Boolean);
      const additions = options?.prefixSegment ? [options.prefixSegment, id] : [id];
      const newPath = `/${[...pathSegments, ...additions].join("/")}${location.search}`;
      navigate(newPath, { preventScrollReset: true });
    },
    [navigate, location.pathname, location.search, options?.prefixSegment]
  );

  const setSelectedId = useCallback(
    (id: string) => {
      const pathSegments = location.pathname.split("/").filter(Boolean);
      // Remove the current ID segment(s) if present
      const segmentsToPop = options?.prefixSegment ? 2 : 1;
      for (let i = 0; i < segmentsToPop; i += 1) {
        pathSegments.pop();
      }
      // Add the new ID
      const additions = options?.prefixSegment ? [options.prefixSegment, id] : [id];
      const newPath = `/${[...pathSegments, ...additions].join("/")}${location.search}`;
      navigate(newPath, { replace: true, preventScrollReset: true });
    },
    [navigate, location.pathname, location.search, options?.prefixSegment]
  );

  const popIdFromUrl = useCallback(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const segmentsToPop = options?.prefixSegment ? 2 : 1;
    for (let i = 0; i < segmentsToPop; i += 1) {
      pathSegments.pop();
    }
    const newPath = `/${pathSegments.join("/")}${location.search}`;
    navigate(newPath, { preventScrollReset: true });
  }, [navigate, location.pathname, location.search, options?.prefixSegment]);

  return {
    selectedId,
    pushIdToUrl,
    setSelectedId,
    popIdFromUrl,
  };
};
